import React, { useEffect, useLayoutEffect, useRef } from 'react';
import { alignClientDocumentSummaries } from './clientDocumentSummaryAlignment';
import moment from 'moment';
import { Box, Stack, TableCell, TableRow, Typography } from '@mui/material';
import {
  clientDocumentBranding,
  ClientDocumentLogo
} from './clientDocumentBranding';

/** Match the accepted client banner: street, then the remaining address. */
export function documentLocationLines(value: string | null | undefined): React.ReactNode {
  const comma = value?.indexOf(',') ?? -1;
  if (!value || comma < 0) return value;
  return <><span style={{ whiteSpace: 'nowrap' }}>{value.slice(0, comma + 1)}</span><br /><span style={{ whiteSpace: 'nowrap' }}>{value.slice(comma + 1).trim()}</span></>;
}

type ClientDocumentBranding = ReturnType<typeof clientDocumentBranding>;

/** The accepted Actuals summary layout, shared without tying labels to columns. */
export function ClientDocumentSubtotal({ branding, columnCount, quantity, value, plain = false, reference = false }: {
  branding: ClientDocumentBranding;
  columnCount: number;
  quantity?: React.ReactNode;
  value: React.ReactNode;
  plain?: boolean;
  reference?: boolean;
}) {
  return (
    <TableRow data-document-total="subtotal" data-quote-reference={reference ? "subtotal" : undefined} data-quote-summary-row={plain || undefined} sx={{ backgroundColor: branding.secondaryColor, '& .MuiTableCell-root': { color: branding.secondaryTextColor } }}>
      <TableCell colSpan={columnCount} sx={{ border: `2px solid ${branding.secondaryColor}` }}>
        <Stack data-document-independent-summary direction="row" justifyContent={quantity === undefined ? 'flex-end' : 'space-between'} alignItems="center" gap={2}>
          {quantity !== undefined && <Stack direction="row" alignItems="center" gap={2}>
            <Typography variant="h5" data-client-summary-label>Crew Subtotal</Typography>
            <Typography>{quantity}</Typography>
          </Stack>}
          <Stack data-client-summary-group direction="row" alignItems="center" gap={2} sx={{ '& > *': { position: 'relative', right: 'var(--client-summary-inset, 0px)' } }}>
            <Typography variant="h5">Sub Total</Typography>
            <Typography variant="h5" data-client-summary-value sx={{ minWidth: '9ch', textAlign: 'right', whiteSpace: 'nowrap' }}>{value}</Typography>
          </Stack>
        </Stack>
      </TableCell>
    </TableRow>
  );
}

export function ClientDocumentTotals({ branding, rows, plain = false, leadingAction }: {
  branding: ClientDocumentBranding;
  leadingAction?: React.ReactNode;
  rows: Array<{ label: React.ReactNode; value: React.ReactNode; total?: boolean; bold?: boolean; warning?: boolean }>;
  plain?: boolean;
}) {
  const summaryRef = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    const root = summaryRef.current?.closest<HTMLElement>('.communication-document') ?? summaryRef.current?.parentElement;
    if (root) alignClientDocumentSummaries(root);
  });
  useEffect(() => {
    const root = summaryRef.current?.closest<HTMLElement>('.communication-document') ?? summaryRef.current?.parentElement;
    const table = root?.querySelector('table');
    if (!root || !table) return;
    let frame = 0;
    let active = true;
    const measure = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => alignClientDocumentSummaries(root));
    };
    const resize = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(measure);
    resize?.observe(table);
    const mutations = new MutationObserver(measure);
    mutations.observe(table, { childList: true, subtree: true, characterData: true });
    window.addEventListener('resize', measure);
    void document.fonts?.ready.then(() => { if (active) measure(); });
    return () => { active = false; resize?.disconnect(); mutations.disconnect(); cancelAnimationFrame(frame); window.removeEventListener('resize', measure); };
  }, []);
  return (
    <Box ref={summaryRef} data-client-summary-group data-document-independent-summary data-client-document-totals className="client-pdf-summary" sx={{ mx: 6, position: 'relative', minHeight: leadingAction ? 36 : undefined, '& [data-client-summary-row] > *': { position: 'relative', right: 'var(--client-summary-inset, 0px)' } }}>
      {leadingAction && <Box data-pdf-exclude sx={{ position: 'absolute', left: 0, top: 0 }}>{leadingAction}</Box>}
      {rows.map((row, index) => {
        const variant = row.total || row.bold ? 'h5' : 'body1';
        return (
          <Stack key={typeof row.label === 'string' ? row.label : index} data-client-summary-row direction="row" justifyContent="flex-end" alignItems="center" gap={2} sx={{ p: 'var(--document-padding, 4px 3px)', backgroundColor: `var(--document-total, ${row.total || plain ? '#fff' : branding.secondaryColor})`, color: `var(--document-totalText, ${row.total || plain ? '#000' : branding.secondaryTextColor})` }}>
            <Typography component="div" variant={variant} sx={row.warning ? { outline: '2px solid', outlineColor: 'warning.main' } : undefined}>{row.label}</Typography>
            <Typography component="div" variant={variant} data-client-summary-value sx={{ minWidth: '9ch', textAlign: 'right', whiteSpace: 'nowrap' }}>{row.value}</Typography>
          </Stack>
        );
      })}
    </Box>
  );
}

/** Keep missing legacy issue dates empty of invented calendar values. */
export function clientDocumentDate(value: unknown, format = 'MM/DD/YY'): string {
  if (!(value instanceof Date) && (typeof value !== 'string' || !value.trim())) return '—';
  const date = moment(value);
  return date.isValid() ? date.format(format) : '—';
}

export type ClientDocumentDetail = {
  label: string;
  value: React.ReactNode;
};

export const CLIENT_DOCUMENT_WRAP_SX = {
  minWidth: 0,
  maxWidth: '100%',
  overflowWrap: 'anywhere',
  wordBreak: 'break-word'
} as const;

export const CLIENT_DOCUMENT_BANNER_SX = {
  position: 'relative',
  display: { xs: 'flex', lg: 'grid' },
  flexDirection: 'column',
  gridTemplateColumns: {
    lg: '110px minmax(200px, 0.75fr) minmax(320px, 1.5fr) minmax(150px, 0.7fr)'
  },
  gridTemplateAreas: {
    lg: '"logo company event title" "logo company event date"'
  },
  alignItems: { xs: 'stretch', lg: 'center' },
  gap: { xs: 2, sm: 3, md: 4 },
  mx: { xs: 1, sm: 3, md: 6 },
  mt: 6,
  p: { xs: 2, sm: 3, md: 4 },
  opacity: 0.9,
  minWidth: 0
} as const;

export const CLIENT_DOCUMENT_FOOTER_SX = {
  display: 'grid',
  gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: '90px minmax(0, 1fr) 90px' },
  alignItems: 'center',
  gap: { xs: 2, md: 3 },
  mx: { xs: 1, sm: 3, md: 5 },
  my: 5,
  p: { xs: 2, sm: 3, md: 4 },
  opacity: 0.9,
  minWidth: 0
} as const;

export function ClientDocumentBanner({
  branding,
  title,
  date,
  details,
  actions,
  className
}: {
  branding: ClientDocumentBranding;
  title: React.ReactNode;
  date: React.ReactNode;
  details: ClientDocumentDetail[];
  actions?: React.ReactNode;
  className?: string;
}) {
  return (
    <Box
      data-document-header
      data-document-banner
      data-client-document-layout="responsive"
      className={className}
      bgcolor={branding.primaryColor}
      sx={CLIENT_DOCUMENT_BANNER_SX}
    >
      {actions}
      <Box
        data-banner-slot="logo"
        sx={{
          gridArea: 'logo',
          display: 'flex',
          alignSelf: { xs: 'center', lg: 'auto' },
          justifyContent: 'center',
          justifySelf: { xs: 'center', md: 'start' },
          minWidth: 0
        }}
      >
        <ClientDocumentLogo branding={branding} height={90} width={90} />
      </Box>

      <Stack
        data-document-company-details
        data-banner-slot="company"
        spacing={0.5}
        sx={{
          gridArea: 'company',
          textAlign: { xs: 'left', md: 'left' },
          ...CLIENT_DOCUMENT_WRAP_SX
        }}
      >
        <Typography
          color={branding.secondaryColor}
          sx={{
            fontSize: { xs: '1.25rem', sm: '1.5rem', md: '1.75rem' },
            fontWeight: 700,
            ...CLIENT_DOCUMENT_WRAP_SX
          }}
        >
          {branding.name}
        </Typography>
        {[branding.address, branding.phone, branding.email].map(
          (line, index) => (
            <Typography
              key={`${index}-${line}`}
              color={branding.primaryTextColor}
              sx={{
                fontSize: { xs: '0.875rem', sm: '1rem' },
                ...CLIENT_DOCUMENT_WRAP_SX
              }}
            >
              {line}
            </Typography>
          )
        )}
      </Stack>

      <Box
        data-document-event-details
        data-banner-slot="event"
        sx={{
          gridArea: 'event',
          display: 'grid',
          gridTemplateColumns: {
            xs: 'minmax(0, 1fr)',
            sm: 'max-content minmax(0, 1fr)'
          },
          columnGap: { xs: 0, sm: 1.5 },
          rowGap: { xs: 0.25, sm: 0.75 },
          alignItems: 'baseline',
          ...CLIENT_DOCUMENT_WRAP_SX
        }}
      >
        {details.map(({ label, value }) => (
          <React.Fragment key={label}>
            <Typography
              color={branding.secondaryColor}
              sx={{
                fontSize: { xs: '0.95rem', sm: '1rem', lg: '1.1rem' },
                fontWeight: 700,
                ...CLIENT_DOCUMENT_WRAP_SX
              }}
            >
              {label}
            </Typography>
            <Typography
              data-document-detail-value
              color={branding.primaryTextColor}
              sx={{
                mb: { xs: 0.75, sm: 0 },
                fontSize: { xs: '0.95rem', sm: '1rem', lg: '1.1rem' },
                ...CLIENT_DOCUMENT_WRAP_SX
              }}
            >
              {value}
            </Typography>
          </React.Fragment>
        ))}
      </Box>

      <Typography
        data-banner-slot="title"
        data-document-title
        color={branding.secondaryColor}
        sx={{
          gridArea: 'title',
          alignSelf: { xs: 'start', lg: 'center' },
          textAlign: { xs: 'left', lg: 'right' },
          fontSize: { xs: '2rem', sm: '2.5rem', lg: '3rem' },
          fontWeight: 500,
          lineHeight: 1.1,
          ...CLIENT_DOCUMENT_WRAP_SX
        }}
      >
        {title}
      </Typography>
      <Typography
        data-document-date
        color={branding.primaryTextColor}
        sx={{
          gridArea: 'date',
          alignSelf: 'end',
          textAlign: { xs: 'left', md: 'right' },
          fontSize: '0.875rem',
          fontWeight: 500,
          ...CLIENT_DOCUMENT_WRAP_SX
        }}
      >
        {date}
      </Typography>
    </Box>
  );
}

export function ClientDocumentFooter({
  branding
}: {
  branding: ClientDocumentBranding;
}) {
  return (
    <Box
      data-document-footer
      bgcolor={branding.primaryColor}
      sx={CLIENT_DOCUMENT_FOOTER_SX}
    >
      <Box sx={{ justifySelf: 'center', minHeight: 120 }}>
        <ClientDocumentLogo branding={branding} height={120} width={90} />
      </Box>
      <Stack
        alignItems="center"
        spacing={1}
        sx={{ textAlign: 'center', ...CLIENT_DOCUMENT_WRAP_SX }}
      >
        <Typography
          color={branding.secondaryColor}
          sx={{
            fontSize: { xs: '1.25rem', sm: '1.75rem', md: '2rem' },
            fontWeight: 500,
            ...CLIENT_DOCUMENT_WRAP_SX
          }}
        >
          THANK YOU FOR YOUR BUSINESS!
        </Typography>
        <Typography
          color={branding.primaryTextColor}
          sx={{ fontSize: '1rem', ...CLIENT_DOCUMENT_WRAP_SX }}
        >
          {branding.name}
        </Typography>
        <Typography
          color={branding.primaryTextColor}
          sx={{ fontSize: '1rem', ...CLIENT_DOCUMENT_WRAP_SX }}
        >
          Proprietary and Confidential. DO NOT share, copy or distribute without
          permission from {branding.name || 'the document owner'}
        </Typography>
      </Stack>
      <Box sx={{ display: 'block', justifySelf: 'center', minHeight: 120 }}>
        <ClientDocumentLogo branding={branding} height={120} width={90} />
      </Box>
    </Box>
  );
}
